// The documents this surface has to carry, the two consents that must not be
// merged, and the one question it refuses to answer on the owner's behalf.
//
// WHY THE WEB SURFACE CARRIES MORE THAN THE TABLET DOES. On a tablet a crew
// member hands the device over and can answer a question. A phone cannot. So
// everything a person might have asked has to be on the page: a privacy notice,
// terms, and a cookie notice. xp/ui/src/legal/documents.ts already models a
// document and already knows that on an attendee's own device a document opens
// in a new tab rather than a modal — `documentOpening` returns "tab" for
// `attendee_device` — so this file supplies the set rather than reinventing the
// presentation.
//
// ============================== THE TWO CONSENTS, AND WHY ONE TICK IS INVALID
//
// They are different instruments and merging them fails both:
//
//   STORAGE CONSENT is ePrivacy Article 5(3) / PECR regulation 6. Its subject is
//   the act of placing something on the attendee's phone. Its scope is this
//   browser. It is withdrawn by clearing the storage.
//
//   PROCESSING CONSENT is GDPR Article 6(1)(a) and Article 7. Its subject is
//   capturing the attendee's details and what the client may afterwards do with
//   them, including marketing contact. Its scope is a controller's records. It
//   is withdrawn by contacting that controller.
//
// A single tick covering both is not valid for either: it cannot be specific
// (Article 4(11)) because it covers two purposes, and it cannot be withdrawn "as
// easily as given" (Article 7(3)) because the two withdrawals go to different
// places and one of them cannot be done from this page at all.
//
// ON THIS SURFACE THE STORAGE SIDE IS NOT A CONSENT AT ALL, and that is the
// prize the storage ruling bought. src/privacy/storage.ts holds nothing, so
// there is nothing to ask about and the storage side is a *notice*.
// `storageSection` below computes which it is from NON_ESSENTIAL rather than
// from a constant, so the day something non-essential is added the page becomes
// a gate instead of silently continuing to claim it is a notice.
//
// The processing side remains a real consent and is presented by the same
// ConsentSet the tablet uses, with the same wording rules, the same
// `checkConsentWording` and the same absolute prohibition on a required consent
// (GDPR Article 7(4), which xp/ui/src/agreement.ts enforces). The web surface is
// therefore at least as strong as the tablet by construction: it is the same
// component under the same checks, plus a notice the tablet does not show.
//
// ================================================ the question that is refused
//
// WHO IS THE DATA CONTROLLER — NOVA, OR THE CLIENT WHOSE STAND IT IS? The
// attendee is on a Nova-owned domain, looking at a Nova-hosted page, handing
// details to the client. The privacy notice has to name the parties correctly
// and describe the relationship, and the answer changes what the document says:
// a Nova-as-processor arrangement names the client as controller and Nova in a
// processing role, and a joint-controller arrangement requires an Article 26
// arrangement and a summary of it made available to the attendee.
//
// Nobody has given that answer, so this file does not invent one. It refuses to
// produce a notice without it — see `readNoticeConfig` — rather than shipping a
// placeholder that reads like a statement. A placeholder in a privacy notice is
// the thing that ships.

import type { AgreementDocument, DocumentKind } from "@nova/xp-ui";
import { DOCUMENT_KINDS } from "../../../schema/src/events.js";
import { onsiteResetFromBundle } from "../../../schema/src/onsiteReset.js";
import {
  instructionWantsLegacyDisplayName,
  presentNameFields,
} from "../../../ui/src/fields/name.js";
import { FIELD_TYPES, type FieldDefinition } from "../../../ui/src/fields/types.js";
import { NON_ESSENTIAL, PERMITTED_STORAGE, consentIsRequiredBeforeStoring } from "./storage.js";

/**
 * A marker that survives into the rendered page.
 *
 * Every piece of text in this file that a lawyer has to write is wrapped in
 * this, and check.mjs asserts that no document body reaches the page without it
 * while any placeholder remains. That is the mechanism that stops a draft
 * shipping: not a promise to remember, but a page that visibly says it is a
 * draft until the drafts are gone.
 */
export const PLACEHOLDER = "[[FOR LEGAL REVIEW]]";

const draft = (text: string): string => `${PLACEHOLDER} ${text}`;

/**
 * The version every document here carries, and it is not a number.
 *
 * `version` on an AgreementDocument is the revision an attendee saw, carried
 * onto the attestation event so that a challenge months later can be answered.
 * These documents have no revision, because nobody has written them: they are
 * placeholders around the structure. A number like "1" would be a lie of exactly
 * the kind the PLACEHOLDER marks exist to prevent — it would read, in a record,
 * as though a first approved revision had been shown to somebody.
 */
const DRAFT_VERSION = "unreviewed-draft";

/**
 * Who the parties are, which is configuration because it is a ruling.
 *
 * `controller` is deliberately not a string with a default. It is a union with
 * no member meaning "unknown", so an unanswered question is an absent value that
 * `readNoticeConfig` refuses, rather than a value that renders as an empty space
 * in a sentence.
 */
export type ControllerArrangement = "client_is_controller" | "nova_is_controller" | "joint_controllers";

export type NoticeConfig = {
  /** The client whose stand it is, as it should appear to an attendee. */
  readonly clientName: string;
  /** The ruling. See the header: this file will not choose it. */
  readonly controller: ControllerArrangement;
  /** Where a person exercises their rights. A mailbox somebody reads. */
  readonly contactAddress: string;
};

export type NoticeReading =
  | { readonly ok: true; readonly documents: readonly AgreementDocument[] }
  | { readonly ok: false; readonly problem: string };

/**
 * The consent wording this page may show, checked, or why it may show none.
 *
 * THIS USED TO REFUSE UNCONDITIONALLY, and the paragraph it refused with is
 * worth keeping in view because it is the specification for what replaced it.
 * It said: the tablet shows the exact paragraph held in 005's `consent_wording`
 * table and records the sha-256 of what it rendered, this page cannot, because
 * every read of an activation's configuration sat behind an operator session and
 * this surface has none by design. Hard-coding the paragraphs here would make
 * the recorded hash a hash of text this package owns; registering first and
 * collecting consent later holds a contact record with no lawful basis in the
 * meantime; drawing the controls inert looks like consent was captured. So it
 * refused, and said the thing that would unblock it was "a public read of an
 * activation's consent wording — no session, rate-limited, activation-scoped,
 * and serving nothing but text that is meant to be shown to the public anyway".
 *
 * That route now exists: `GET /v1/attend/activations/:activationId/consent-wording`,
 * and api/src/attend/wording.ts carries its argument. This function is what
 * replaced the refusal, and it is not a pass-through.
 *
 * WHY THE SERVER'S ANSWER IS STILL CHECKED HERE. Not because the server is
 * untrusted — it is ours, and 005's check constraint binds `wording_hash` to
 * `sha256(body)` in the database, which is a stronger guarantee than anything
 * this file could add. It is checked because *this* page is what a consent record
 * will assert something about. Three of the four checks below are about
 * properties of the rendered page rather than of the served bytes:
 *
 *   THE HASH IS RECOMPUTED FROM THE BODY THIS PAGE HOLDS. If the two disagree,
 *   the page is about to send a hash for text other than the text it will
 *   display, which is the one failure that produces a consent record that reads
 *   as evidence and is not. It cannot happen through the server — 005 forbids it
 *   — and it can happen through a transform, a proxy that re-encodes UTF-8, or a
 *   future caller of this function that trims the body first.
 *
 *   THE TWO INSTRUMENTS STAY APART. `processingConsentIsSeparate` has been in
 *   this file since it was written and, until now, nothing called it with real
 *   wording: it was a check with no subject. The served bodies are its subject.
 *   A client whose approved marketing paragraph mentions cookies produces a tick
 *   that is invalid for the processing as well as for the storage, and it is
 *   caught here rather than by a lawyer.
 *
 *   AN EMPTY SET IS A REFUSAL AND NOT AN EASY DAY. An open stand whose bundle
 *   cites no consent this surface can present is a stand that would collect a
 *   name, a phone number and a verified address with nothing recorded about what
 *   may be done with them — including nothing permitting the transfer to the
 *   client, which is the entire purpose of the stand. register.ts would accept
 *   it: `consent: []` is a valid request there and means "registered and agreed
 *   to nothing", which is the right shape for somebody who declined. It is the
 *   wrong shape for somebody who was never asked, and those two are
 *   indistinguishable in the spine once written.
 *
 * ASYNC, BECAUSE SHA-256 IN A BROWSER IS. `crypto.subtle.digest` returns a
 * promise and there is no synchronous alternative that is not a hand-rolled
 * implementation of a hash the database is the other end of.
 */
export type ConsentWordingReading =
  | {
      readonly ok: true;
      readonly documents: readonly ConsentDocument[];
      /**
       * The bundle version the paragraphs above came out of, to be cited on the
       * registration. Never null on this branch: a response with no version is
       * refused below rather than carried as an optional.
       */
      readonly configVersion: number;
      /**
       * The instruction those fields came out of, or 0 when the stand served none.
       *
       * Zero means "no field set", matching register.ts. A positive number is
       * what the registration echoes, so the spine can join the row to the
       * instruction that permitted it.
       */
      readonly fieldSetVersion: number;
      /**
       * The attendee-facing fields to draw after the mailbox. Never includes
       * the mailbox itself or a crew-only status — those are stripped here
       * even if a future server forgets to.
       */
      readonly fields: readonly FieldDefinition[];
      /**
       * The live instruction still has one display_name box. The page drew
       * first and last instead; Submit must fold them back so the register
       * route does not refuse two keys nobody published.
       */
      readonly foldDisplayName: boolean;
      /**
       * Which paths the published setup is open on. Missing on an older
       * response reads as both, which is what those shows ran.
       */
      readonly captureOffer: "public" | "onsite" | "both";
      readonly onsiteIdleHomeMs: number;
      readonly onsiteCrewResetEnabled: boolean;
      /**
       * Legal documents compiled into the bundle. Empty when none were
       * enabled. A published cookie_notice is the client's policy; if it
       * is missing, the page still appends the generated storage notice.
       */
      readonly legalDocuments: readonly AgreementDocument[];
      /**
       * Required pack ticks. Empty on a bundle that never published attestations.
       * Submit may wait for these. They are not consents.
       */
      readonly attestations: readonly AttestationDocument[];
      /** Short privacy notice on Terms. Null when the stand served none. */
      readonly privacyNotice: string | null;
      /** Client marks. Null when the bundle named none. */
      readonly logos: { readonly light?: string; readonly dark?: string } | null;
      /** Token overrides for the light page. Null when the stand named none. */
      readonly theme: Readonly<Record<string, string>> | null;
      /** Token overrides for the dark page. Null when the stand named none. */
      readonly themeDark: Readonly<Record<string, string>> | null;
      /**
       * Which public join URL this stand prints. Missing on an older
       * response reads as registration.
       */
      readonly joinKind: "registration" | "wheel";
      /** Slice labels the dial draws. Empty when the stand served none. */
      readonly wheelSlices: readonly { readonly id: string; readonly label: string }[];
    }
  | { readonly ok: false; readonly problem: string };

export type AttestationDocument = {
  readonly kind: "eligibility" | "terms_acceptance";
  readonly attestation_version_id: string;
  readonly wording_hash: string;
  readonly locale: string;
  readonly body: string;
  readonly heading?: string;
};

export type ConsentDocument = {
  readonly purpose: string;
  readonly consent_version_id: string;
  readonly wording_hash: string;
  readonly locale: string;
  readonly body: string;
  readonly heading?: string;
};

/**
 * The purposes this page may draw a tick box for.
 *
 * A copy of WEB_CONSENT_PURPOSES in api/src/attend/register.ts, restated for the
 * reason ATTEND_ENDPOINTS is restated in flow.ts — this package depends on
 * neither xp/api nor its Postgres driver — and compared against it by check.mjs
 * rather than trusted, which is the arrangement that makes the copy a decision.
 *
 * Narrowed here as well as on the server, and the two narrowings do different
 * jobs. The server's stops a purpose being served that the register route would
 * refuse. This one stops a purpose being *rendered* that this page has no rules
 * about, which matters because the checks below — and particularly the storage
 * separation — are written against these three and nothing else.
 */
export const PRESENTABLE_PURPOSES = [
  "client_data_transfer",
  "contact",
  "marketing",
  "marketing_email",
  "marketing_sms",
  "marketing_phone",
  "enquiry_follow_up",
  "image_marketing",
  "profiling",
] as const;

/** What `encode(sha256(...), 'hex')` produces, and 005 stores. Lower case. */
const WORDING_HASH = /^[0-9a-f]{64}$/;

function optionalHeading(cited: Record<string, unknown>): { heading?: string } {
  const raw = cited["heading"];
  if (typeof raw !== "string") return {};
  const heading = raw.trim();
  return heading === "" ? {} : { heading };
}

export async function readConsentWording(
  served: unknown,
  options: { readonly keepMailbox?: boolean } = {},
): Promise<ConsentWordingReading> {
  if (served === null || typeof served !== "object" || Array.isArray(served)) {
    return refuse("the server's answer was not an object.");
  }

  const list = (served as Record<string, unknown>)["consent"];
  if (!Array.isArray(list)) {
    return refuse("the server's answer carried no `consent` list.");
  }

  // CHECKED BEFORE THE PARAGRAPHS, BECAUSE IT IS THE MORE BASIC FACT AND THE
  // MORE USEFUL SENTENCE.
  //
  // A stand with nothing published serves `config_version: null` and, necessarily,
  // an empty consent list — so both this check and the empty-set check below would
  // fire, and only one of them names the actual cause. "Nothing has been published
  // at this stand" is something a crew member can act on; "the configuration cites
  // no wording this page can present" sends them looking for a wording fault at a
  // stand that has no configuration at all.
  //
  // AND WITHOUT A VERSION THIS PAGE CANNOT REGISTER ANYBODY EVEN IF IT WANTED TO.
  // The register route requires `config_version` and refuses a number naming no
  // bundle, so a page that carried on would collect a name, a phone number and a
  // confirmed address and then be refused after the form was filled in — which is
  // the failure mode this file already refuses to build on for served purposes the
  // register route would reject.
  const version = (served as Record<string, unknown>)["config_version"];
  if (version === null) {
    return refuse(
      "this stand has no published configuration at all, so there is nothing saying what may be collected " +
        "here or what an attendee may be asked to agree to. What fixes it is recording the client's " +
        "instruction and publishing a bundle for this activation.",
    );
  }
  // Not merely `typeof === "number"`. A non-integer or a zero would be a number
  // naming a version that cannot exist — bundle versions are counted from one —
  // and sending one is refused by the server anyway, so it is caught here where
  // the sentence can say which part of the answer was wrong.
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1) {
    return refuse(
      `the server named this stand's configuration version as ${JSON.stringify(version)}, which is not a ` +
        "version number, so this page cannot record which configuration somebody was shown.",
    );
  }

  const documents: ConsentDocument[] = [];
  const faults: string[] = [];

  for (const entry of list) {
    if (entry === null || typeof entry !== "object" || Array.isArray(entry)) {
      faults.push("one entry was not an object.");
      continue;
    }
    const cited = entry as Record<string, unknown>;

    const purpose = cited["purpose"];
    const versionId = cited["consent_version_id"];
    const hash = cited["wording_hash"];
    const locale = cited["locale"];
    const body = cited["body"];

    if (
      typeof purpose !== "string" ||
      typeof versionId !== "string" ||
      typeof hash !== "string" ||
      typeof locale !== "string" ||
      typeof body !== "string"
    ) {
      faults.push(`an entry for ${JSON.stringify(purpose)} was missing a field or held the wrong type.`);
      continue;
    }

    if (!(PRESENTABLE_PURPOSES as readonly string[]).includes(purpose)) {
      // Refused rather than skipped, unlike the server's equivalent narrowing.
      // The server drops a purpose it cannot present because a bundle may
      // legitimately cite one for a tablet; by the time it reaches here it has
      // already been through that filter, so a purpose arriving at this line
      // means the two lists have drifted — and a page that quietly rendered
      // two of three tick boxes is a page collecting partial consent.
      faults.push(
        `${JSON.stringify(purpose)} is not a purpose this page has wording rules for, which means this ` +
          "page and the register route disagree about the set.",
      );
      continue;
    }

    if (!WORDING_HASH.test(hash)) {
      faults.push(`the hash for ${JSON.stringify(purpose)} is not 64 lower-case hex characters.`);
      continue;
    }

    if (body.trim() === "") {
      // A tick box with no words beside it is the inert-control case in a
      // different costume: it looks like something was agreed to.
      faults.push(`the wording for ${JSON.stringify(purpose)} is empty, so there is nothing to agree to.`);
      continue;
    }

    const computed = await sha256Hex(body);
    if (computed !== hash) {
      faults.push(
        `the wording for ${JSON.stringify(purpose)} hashes to ${computed} and was served as ${hash}, so ` +
          "this page would be recording agreement to text other than the text it displayed.",
      );
      continue;
    }

    documents.push({
      purpose,
      consent_version_id: versionId,
      wording_hash: hash,
      locale,
      body,
      ...optionalHeading(cited),
    });
  }

  const attestations: AttestationDocument[] = [];
  const attestationList = (served as Record<string, unknown>)["attestations"];
  if (attestationList !== undefined && attestationList !== null) {
    if (!Array.isArray(attestationList)) {
      faults.push("the server's `attestations` list was not a list.");
    } else {
      for (const entry of attestationList) {
        if (entry === null || typeof entry !== "object" || Array.isArray(entry)) {
          faults.push("one attestation was not an object.");
          continue;
        }
        const cited = entry as Record<string, unknown>;
        const kind = cited["kind"];
        const versionId = cited["attestation_version_id"];
        const hash = cited["wording_hash"];
        const locale = cited["locale"];
        const body = cited["body"];
        if (
          typeof kind !== "string" ||
          typeof versionId !== "string" ||
          typeof hash !== "string" ||
          typeof locale !== "string" ||
          typeof body !== "string"
        ) {
          faults.push(`an attestation for ${JSON.stringify(kind)} was missing a field or held the wrong type.`);
          continue;
        }
        if (kind !== "eligibility" && kind !== "terms_acceptance") {
          faults.push(`${JSON.stringify(kind)} is not an attestation kind this page can present.`);
          continue;
        }
        if (!WORDING_HASH.test(hash)) {
          faults.push(`the hash for attestation ${JSON.stringify(versionId)} is not 64 lower-case hex characters.`);
          continue;
        }
        if (body.trim() === "") {
          faults.push(`the wording for attestation ${JSON.stringify(versionId)} is empty, so there is nothing to agree to.`);
          continue;
        }
        const computed = await sha256Hex(body);
        if (computed !== hash) {
          faults.push(
            `the wording for attestation ${JSON.stringify(versionId)} hashes to ${computed} and was served as ${hash}, so ` +
              "this page would be recording agreement to text other than the text it displayed.",
          );
          continue;
        }
        attestations.push({
          kind,
          attestation_version_id: versionId,
          wording_hash: hash,
          locale,
          body,
          ...optionalHeading(cited),
        });
      }
    }
  }

  const rawNotice = (served as Record<string, unknown>)["privacy_notice"];
  let privacyNotice: string | null = null;
  if (rawNotice !== undefined && rawNotice !== null) {
    if (typeof rawNotice !== "string") {
      faults.push("privacy_notice was not a string.");
    } else if (rawNotice.trim() !== "") {
      privacyNotice = rawNotice.trim();
    }
  }

  if (faults.length) {
    return refuse(
      "the wording this stand serves cannot be presented honestly:\n\n" +
        faults.map((f) => `  - ${f}`).join("\n"),
    );
  }

  if (documents.length === 0) {
    if (attestations.length === 0) {
      return refuse(
        `this stand's live configuration is version ${version} and it cites no consent wording this page ` +
          "can present, so registering somebody here would collect a name, a telephone number and a " +
          "confirmed email address with nothing recorded about what may be done with them — including " +
          "nothing permitting them to be passed to the client, which is what the stand is for. What fixes " +
          "it is a published bundle citing registered wording for client_data_transfer; a crew tablet at " +
          "the same stand would be showing it.",
      );
    }
  }

  const separate = processingConsentIsSeparate(documents.map((document) => document.body));
  if (!separate.ok) {
    return refuse(separate.problem ?? "a processing consent and a storage consent were merged.");
  }

  const servedFields = readServedFields(served as Record<string, unknown>, options.keepMailbox === true);
  if (!servedFields.ok) return refuse(servedFields.problem);

  const captureOffer = readCaptureOffer((served as Record<string, unknown>)["capture_offer"]);
  const onsiteReset = onsiteResetFromBundle({
    onsite_idle_home_ms: (served as Record<string, unknown>)["onsite_idle_home_ms"],
    onsite_crew_reset_enabled: (served as Record<string, unknown>)["onsite_crew_reset_enabled"],
  });
  const legalDocuments = readLegalDocuments(served as Record<string, unknown>);
  const logos = readLogos((served as Record<string, unknown>)["logos"]);
  const theme = readThemeTokens((served as Record<string, unknown>)["theme"]);
  const themeDark = readThemeTokens((served as Record<string, unknown>)["theme_dark"]);
  const joinKind = readJoinKind((served as Record<string, unknown>)["join_kind"]);
  const wheelSlices = readWheelSlices((served as Record<string, unknown>)["wheel"]);

  return {
    ok: true,
    documents,
    configVersion: version,
    fieldSetVersion: servedFields.fieldSetVersion,
    fields: servedFields.fields,
    foldDisplayName: servedFields.foldDisplayName,
    captureOffer,
    onsiteIdleHomeMs: onsiteReset.idleHomeMs,
    onsiteCrewResetEnabled: onsiteReset.crewResetEnabled,
    legalDocuments,
    attestations,
    privacyNotice,
    logos,
    theme,
    themeDark,
    joinKind,
    wheelSlices,
  };
}

function readThemeTokens(value: unknown): Readonly<Record<string, string>> | null {
  if (value === undefined || value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const out: Record<string, string> = {};
  for (const [token, tokenValue] of Object.entries(value as Record<string, unknown>)) {
    if (typeof tokenValue === "string" && tokenValue.trim() !== "") out[token] = tokenValue.trim();
  }
  return Object.keys(out).length === 0 ? null : out;
}

function readLogos(value: unknown): { readonly light?: string; readonly dark?: string } | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "object" || Array.isArray(value)) return null;
  const body = value as Record<string, unknown>;
  const light = typeof body.light === "string" && body.light.trim() !== "" ? body.light.trim() : undefined;
  const dark = typeof body.dark === "string" && body.dark.trim() !== "" ? body.dark.trim() : undefined;
  if (light === undefined && dark === undefined) return null;
  return { ...(light === undefined ? {} : { light }), ...(dark === undefined ? {} : { dark }) };
}

function readCaptureOffer(value: unknown): "public" | "onsite" | "both" {
  if (value === "public" || value === "onsite" || value === "both") return value;
  return "both";
}

function readJoinKind(value: unknown): "registration" | "wheel" {
  return value === "wheel" ? "wheel" : "registration";
}

function readWheelSlices(value: unknown): { readonly id: string; readonly label: string }[] {
  if (value === undefined || value === null || typeof value !== "object" || Array.isArray(value)) {
    return [];
  }
  const list = (value as Record<string, unknown>)["slices"];
  if (!Array.isArray(list)) return [];
  const out: { id: string; label: string }[] = [];
  for (const item of list) {
    if (item === null || typeof item !== "object" || Array.isArray(item)) continue;
    const row = item as { id?: unknown; label?: unknown };
    if (typeof row.id !== "string" || typeof row.label !== "string") continue;
    const id = row.id.trim();
    if (id === "") continue;
    out.push({ id, label: row.label });
  }
  return out;
}

function readLegalDocuments(served: Record<string, unknown>): AgreementDocument[] {
  const list = served["documents"];
  if (list === undefined || list === null) return [];
  if (!Array.isArray(list)) return [];
  const out: AgreementDocument[] = [];
  for (const entry of list) {
    if (entry === null || typeof entry !== "object" || Array.isArray(entry)) continue;
    const raw = entry as Record<string, unknown>;
    const id = raw["document_id"];
    const kind = raw["kind"];
    const title = raw["title"];
    const version = raw["version"];
    const body = raw["body"];
    if (typeof id !== "string" || id === "") continue;
    if (!isDocumentKind(kind)) continue;
    if (typeof title !== "string" || typeof version !== "string" || typeof body !== "string") continue;
    if (body.trim() === "") continue;
    const href = raw["href"];
    out.push({
      document_id: id,
      kind,
      title,
      version,
      body,
      ...(typeof href === "string" && href !== "" ? { href } : {}),
    });
  }
  return out;
}

function isDocumentKind(value: unknown): value is DocumentKind {
  return typeof value === "string" && (DOCUMENT_KINDS as readonly string[]).includes(value);
}

/**
 * The field set the wording read carried, or none.
 *
 * ABSENT KEYS ARE NONE, not a refusal. The unit fixtures in check.mjs send
 * consent and a version and nothing else, and a page talking to an older
 * server that has not grown these keys yet must still be able to register
 * somebody against an empty field set — that is the honest citation of "this
 * stand asked nothing configured". A key that is present and the wrong shape
 * is refused, because drawing a control this page cannot name is how a
 * stranger's phone collects an answer nobody instructed.
 *
 * `contact_email` AND `status` ARE DROPPED EVEN IF SERVED. The mailbox is
 * already proved; a status is crew-only. The server strips both, and this is
 * the second lock.
 */
function readServedFields(
  served: Record<string, unknown>,
  keepMailbox: boolean,
):
  | { ok: true; fieldSetVersion: number; fields: FieldDefinition[]; foldDisplayName: boolean }
  | { ok: false; problem: string } {
  const rawVersion = served["field_set_version"];
  if (rawVersion !== undefined && rawVersion !== null) {
    if (typeof rawVersion !== "number" || !Number.isInteger(rawVersion) || rawVersion < 0) {
      return {
        ok: false,
        problem:
          `the server named this stand's field set version as ${JSON.stringify(rawVersion)}, which is not ` +
          "a version number, so this page cannot record which instruction somebody was shown.",
      };
    }
  }
  const fieldSetVersion = typeof rawVersion === "number" ? rawVersion : 0;

  const rawFields = served["fields"];
  if (rawFields !== undefined && !Array.isArray(rawFields)) {
    return {
      ok: false,
      problem: "the server's answer carried a field set that was not a list, so this page cannot draw it.",
    };
  }

  const fields: FieldDefinition[] = [];
  for (const entry of rawFields ?? []) {
    if (entry === null || typeof entry !== "object" || Array.isArray(entry)) {
      return {
        ok: false,
        problem: "the server's field set held an entry that was not an object, so this page will not draw it.",
      };
    }
    const field = entry as Record<string, unknown>;
    const id = field["field_id"];
    const type = field["type"];
    if (typeof id !== "string" || id.length === 0) {
      return {
        ok: false,
        problem: "the server's field set held a field with no id, so this page will not draw it.",
      };
    }
    if (typeof type !== "string" || !(FIELD_TYPES as readonly string[]).includes(type)) {
      return {
        ok: false,
        problem:
          `the server's field set held ${JSON.stringify(id)} with type ${JSON.stringify(type)}, which is ` +
          "not a type this page can collect.",
      };
    }
    if (type === "status") continue;
    if (id === "contact_email" && !keepMailbox) continue;
    fields.push(entry as FieldDefinition);
  }

  const foldDisplayName = instructionWantsLegacyDisplayName(fields);
  return { ok: true, fieldSetVersion, fields: presentNameFields(fields), foldDisplayName };
}

function refuse(problem: string): ConsentWordingReading {
  return {
    ok: false,
    problem:
      "This page will not register anybody at this stand, because " +
      problem +
      "\n\n  This is a configuration fault rather than anything the attendee did, and refusing is the " +
      "recoverable direction: nobody is registered with no lawful basis, and the page starts working the " +
      "moment the stand's wording is right.",
  };
}

/**
 * sha-256 of a string as lower-case hex, which is `consent_wording`'s key.
 *
 * WEB CRYPTO RATHER THAN A COPY OF xp/ui's `wordingHash`, and the choice is
 * worth its own note because reuse is the house rule and this is a departure.
 *
 * `@nova/xp-ui` exports exactly one entry point — `"."` in its package.json —
 * and that barrel is the consent presenter, the field renderer, attract mode and
 * the design tokens, all of which are React. Importing a value from it pulls
 * that graph into a bundle that an attendee downloads over venue wifi, to reach
 * eleven lines of digest. The type-only import at the top of this file costs
 * nothing because types are erased; a value import is a different transaction.
 *
 * WHAT IS ACTUALLY SHARED IS THE EXPRESSION, AND IT IS SHARED THROUGH THE
 * DATABASE. 005 stores `encode(sha256(convert_to(body, 'UTF8')), 'hex')` as a
 * check constraint. xp/ui's hash.ts, xp/api's `wordingHash` and this function are
 * three implementations of that one line, and the thing that keeps them
 * agreeing is not that two of them are the same file — it is that a disagreement
 * by one character is a consent Postgres refuses. That is a stronger coupling
 * than an import, and it is the coupling xp/ui's own header describes.
 *
 * NO NORMALISATION, for the reason hash.ts spends a paragraph on: not trimmed,
 * not reflowed, not NFC. Postgres hashed the bytes it was given, so a client that
 * helpfully normalised would compute a hash for a string nobody registered.
 */
async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * The three documents, or a refusal naming what is missing.
 *
 * A refusal rather than a partial set, because a registration page showing terms
 * and no privacy notice is worse than one showing neither: the first looks
 * complete.
 */
/**
 * Every key optional AND explicitly `| undefined`, which is not the same thing
 * under exactOptionalPropertyTypes and matters here.
 *
 * The caller is build/config.ts, handing over values a bundler may or may not
 * have substituted. `Partial<T>` alone says a key may be absent; it does not say
 * a present key may hold undefined, which is exactly what an unsubstituted
 * build flag is. Requiring the caller to delete absent keys instead would put
 * the burden in the one file that cannot be checked.
 */
export type NoticeConfigInput = { readonly [K in keyof NoticeConfig]?: NoticeConfig[K] | undefined };

export function readNoticeConfig(config: NoticeConfigInput): NoticeReading {
  const missing: string[] = [];

  if (!config.clientName?.trim()) {
    missing.push(
      "clientName — the notice has to say whose stand this is, and an attendee who cannot tell which " +
        "company they just gave their details to cannot exercise any right they have.",
    );
  }

  if (config.controller === undefined) {
    missing.push(
      "controller — nobody has ruled whether Nova or the client is the data controller. This is the " +
        "question xp/WEB-REPORT.md puts to the owner, and it is refused here rather than guessed because " +
        "the answer changes what the privacy notice says about who to contact, who is accountable, and " +
        "whether an Article 26 joint-controller arrangement has to exist and be summarised on this page.",
    );
  }

  if (!config.contactAddress?.trim()) {
    missing.push(
      "contactAddress — a privacy notice with no route to a human is a notice that cannot deliver " +
        "access, erasure or withdrawal, all of which have statutory deadlines.",
    );
  }

  if (missing.length) {
    return {
      ok: false,
      problem:
        "This registration page cannot show its notices:\n\n" +
        missing.map((m) => `  - ${m}`).join("\n\n"),
    };
  }

  const settled = config as NoticeConfig;

  return { ok: true, documents: [privacyNotice(settled), terms(settled), cookieNotice()] };
}

/**
 * How the parties are described, in one place.
 *
 * Three sentences rather than one with a conditional in it, because they are
 * three different legal positions and a template with a variable in it is how
 * they end up worded as if they were the same position with a different name.
 * All three are drafts.
 */
function partiesParagraph(config: NoticeConfig): string {
  switch (config.controller) {
    case "client_is_controller":
      return draft(
        `${config.clientName} decides why and how your details are used, and is the data controller. ` +
          `Nova Exhibits provides and runs this registration page on their instructions and does not use ` +
          `your details for its own purposes.`,
      );
    case "nova_is_controller":
      return draft(
        `Nova Exhibits decides why and how your details are used, and is the data controller. ` +
          `${config.clientName} receives your details in order to follow up on your visit to their stand.`,
      );
    case "joint_controllers":
      return draft(
        `${config.clientName} and Nova Exhibits together decide why and how your details are used, and ` +
          `are joint controllers. You may exercise your rights against either of them. A summary of the ` +
          `arrangement between them is available on request.`,
      );
  }
}

function privacyNotice(config: NoticeConfig): AgreementDocument {
  return {
    document_id: "privacy_notice",
    kind: "privacy_notice",
    version: DRAFT_VERSION,
    title: "Privacy notice",
    body: [
      partiesParagraph(config),
      draft(
        "What we collect: the details you type into this form, and the fact and time of your " +
          "registration at this stand.",
      ),
      draft(
        "Why: to record that you visited the stand, and — only if you tick the box asking for it — to " +
          "contact you afterwards.",
      ),
      draft(
        "Your email address: we send a code to it before you fill the form in, so that we know the " +
          "address works and belongs to you. We keep a record that this happened.",
      ),
      draft(
        "We also count which step of this page you reach, so we can see where people stop. The count " +
          "travels through this same domain to Plausible, who process it in the European Union. They " +
          "receive the address of the step, your IP address and your browser's user-agent, hash those " +
          "with a salt that changes every day, and store none of those three. They do not receive what " +
          "you type into the form. A one-time link token is removed before anything is sent. A Global " +
          "Privacy Control signal means we do not send the count.",
      ),
      draft("How long we keep it: [retention period].", ),
      draft(`Your rights, and how to use them: write to ${config.contactAddress}.`),
      draft("Where your details are held: the United Kingdom."),
    ].join("\n\n"),
  };
}

function terms(config: NoticeConfig): AgreementDocument {
  return {
    document_id: "terms",
    kind: "terms",
    version: DRAFT_VERSION,
    title: "Terms",
    body: [
      draft(`These terms cover your use of this registration page for ${config.clientName}'s stand.`),
      draft("[Terms of use — to be drafted.]"),
    ].join("\n\n"),
  };
}

/**
 * The cookie notice, rendered from the storage list rather than written.
 *
 * THIS IS THE PART THAT CANNOT ROT, and it is the reason storage.ts holds a list
 * of objects rather than a paragraph of prose. A cookie notice is wrong the
 * moment somebody adds a cookie, and every cookie notice on the internet is
 * wrong for exactly that reason. This one is generated from the same list the
 * storage wrapper enforces, so a new item is either on the list — and therefore
 * in this document — or it throws.
 *
 * Its first line is not a draft, and that is deliberate: "we set nothing that
 * needs your permission" is a statement of fact about this code, checkable by
 * reading storage.ts, and marking a checkable fact as awaiting a lawyer would
 * devalue the marks on the sentences that genuinely are.
 */
function cookieNotice(): AgreementDocument {
  const opening = consentIsRequiredBeforeStoring()
    ? `This page stores ${NON_ESSENTIAL.length} thing(s) on your device that are not strictly necessary, ` +
      "so it asks your permission first. You can refuse, and refusing is one tap, the same as accepting."
    : "This page sets no cookies, and stores nothing on your device that is not strictly necessary to " +
      "complete the registration you asked for. There is nothing here to agree to, so this page does not " +
      "ask. There is no advertising and nothing loaded from another company. Visits to each step of this " +
      "page are counted through this same domain; the count is processed in the European Union by " +
      "Plausible; a one-time link token is removed before anything is sent; and a Global Privacy Control " +
      "signal means this visit is not counted. Nothing is written to your phone for that count.";

  const items =
    PERMITTED_STORAGE.length === 0
      ? [
          "Nothing is written to your phone. The confirmation that you entered the emailed code lives " +
            "in this page's memory until you finish or close the tab, and is not written to cookies, " +
            "local storage, session storage or IndexedDB.",
        ]
      : PERMITTED_STORAGE.map(
          (entry) => `${entry.key} (${entry.medium.replace(/_/g, " ")}): ${entry.why} ${entry.lifetime}`,
        );

  return {
    document_id: "cookie_notice",
    kind: "cookie_notice",
    version: DRAFT_VERSION,
    title: "Cookies and storage",
    // Marked for review like the other two, and check.mjs found that it was not.
    //
    // The omission had a reason and the reason was wrong. This document is
    // generated entirely from PERMITTED_STORAGE, so it reads as a statement of
    // fact rather than as drafted copy — and a statement of fact seemed like
    // something a lawyer had nothing to say about. But "there is nothing here to
    // agree to" is a legal conclusion about ePrivacy and PECR, reached by this
    // code, and it is exactly the sentence that would be quoted back if it were
    // wrong. The facts under it are generated; the conclusion drawn from them is
    // not, and that is the part needing review.
    body: [draft(opening), "What is stored:", ...items].join("\n\n"),
  };
}

/**
 * Whether the storage side of the page is a notice or a gate.
 *
 * Two words, and they are the two legal postures. Computed rather than declared,
 * per the header.
 */
export function storageSection(): "notice" | "consent_gate" {
  return consentIsRequiredBeforeStoring() ? "consent_gate" : "notice";
}

/**
 * Whether a set of consent purposes keeps the two instruments apart.
 *
 * THE CHECK THAT STOPS THE MISTAKE THIS FILE IS ABOUT. A consent presented to
 * the attendee under the GDPR heading must not also purport to cover storage —
 * a tick that says "I agree to marketing and to cookies" is invalid for both.
 * This is a textual test over the wording rather than a structural one, because
 * the mistake is made in the copy: nothing about the data structure changes when
 * somebody edits a label to mention cookies.
 *
 * It looks for the vocabulary of storage inside processing-consent wording. A
 * false positive is a consent that mentions cookies for a good reason and has to
 * be reworded; that is a cheap failure. A false negative is an invalid consent
 * collected from every attendee at a show.
 */
const STORAGE_WORDS = /\b(cookies?|local ?storage|session ?storage|your device|this device|browser storage)\b/i;

export function processingConsentIsSeparate(wording: readonly string[]): {
  readonly ok: boolean;
  readonly problem: string | null;
} {
  const offending = wording.filter((text) => STORAGE_WORDS.test(text));
  if (offending.length === 0) return { ok: true, problem: null };

  return {
    ok: false,
    problem:
      "A data-processing consent mentions storage on the attendee's device:\n\n" +
      offending.map((t) => `  - ${JSON.stringify(t)}`).join("\n") +
      "\n\n  These are two different instruments with two different legal bases, two different scopes and " +
      "two different withdrawal routes. One tick covering both is not specific under GDPR Article 4(11) " +
      "and cannot be withdrawn as easily as it was given under Article 7(3), so it is invalid for the " +
      "processing as well as for the storage.\n\n" +
      "  The storage side of this page belongs in the cookie notice, which is generated from " +
      "src/privacy/storage.ts and needs no tick while NON_ESSENTIAL is empty.",
  };
}
